import React, { useState } from 'react';
import { 
  Building2, 
  Users, 
  TrendingUp, 
  DollarSign,
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Download,
  CheckCircle,
  Clock,
  XCircle,
  BarChart3,
  Award,
  FileText
} from 'lucide-react';

const TNSSP = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [projects, setProjects] = useState([
    { id: 1, projectName: 'Rural Road Development', location: 'Tiruchirappalli', budget: '50,00,000', spent: '32,50,000', status: 'ongoing', completion: 65, startDate: '2024-01-15', beneficiaries: 1200 },
    { id: 2, projectName: 'Water Supply System', location: 'Salem', budget: '75,00,000', spent: '30,00,000', status: 'ongoing', completion: 40, startDate: '2024-02-01', beneficiaries: 2500 },
    { id: 3, projectName: 'Community Center', location: 'Vellore', budget: '30,00,000', spent: '30,00,000', status: 'completed', completion: 100, startDate: '2023-10-01', beneficiaries: 800 },
    { id: 4, projectName: 'Healthcare Facility', location: 'Madurai', budget: '1,20,00,000', spent: '24,00,000', status: 'ongoing', completion: 20, startDate: '2024-03-10', beneficiaries: 5000 },
  ]);

  const [beneficiaries, setBeneficiaries] = useState([
    { id: 1, name: 'Murugan S', village: 'Thanjavur', scheme: 'Housing Support', amount: '1,50,000', status: 'approved', appliedDate: '2024-10-15', contact: '9876543210' },
    { id: 2, name: 'Lakshmi R', village: 'Erode', scheme: 'Agricultural Aid', amount: '80,000', status: 'pending', appliedDate: '2024-11-01', contact: '9876543211' },
    { id: 3, name: 'Selvam K', village: 'Dindigul', scheme: 'Housing Support', amount: '1,50,000', status: 'disbursed', appliedDate: '2024-09-20', contact: '9876543212' },
    { id: 4, name: 'Priya M', village: 'Coimbatore', scheme: 'Education Support', amount: '50,000', status: 'approved', appliedDate: '2024-10-25', contact: '9876543213' },
  ]);

  const schemes = [
    { id: 1, name: 'Housing Support Scheme', budget: '100 Cr', beneficiaries: 5000, active: true, description: 'Financial assistance for housing construction' },
    { id: 2, name: 'Agricultural Aid Program', budget: '75 Cr', beneficiaries: 8000, active: true, description: 'Support for farmers and agricultural development' },
    { id: 3, name: 'Education Support', budget: '50 Cr', beneficiaries: 12000, active: true, description: 'Scholarships and educational assistance' },
    { id: 4, name: 'Healthcare Initiative', budget: '80 Cr', beneficiaries: 15000, active: true, description: 'Medical support and healthcare facilities' },
  ];

  const stats = {
    totalProjects: projects.length,
    ongoingProjects: projects.filter(p => p.status === 'ongoing').length,
    totalBudget: projects.reduce((sum, p) => sum + parseFloat(p.budget.replace(/,/g, '')), 0),
    totalBeneficiaries: beneficiaries.length,
    pendingApprovals: beneficiaries.filter(b => b.status === 'pending').length,
    disbursed: beneficiaries.filter(b => b.status === 'disbursed').length,
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          project.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const filteredBeneficiaries = beneficiaries.filter(beneficiary => {
    const matchesSearch = beneficiary.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          beneficiary.village.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          beneficiary.scheme.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || beneficiary.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed':
      case 'approved':
      case 'disbursed':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
      case 'ongoing':
        return <Clock className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Tamil Nadu Social Security Program</h1>
          <p className="text-gray-600 mt-1">Manage social welfare projects and beneficiaries</p>
        </div>
        <button className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#3d82c4] hover:bg-[#2d6fad] text-white rounded-lg font-medium transition-colors shadow-sm">
          <Plus className="w-5 h-5" />
          {activeTab === 'projects' ? 'New Project' : activeTab === 'beneficiaries' ? 'Add Beneficiary' : 'New Scheme'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Projects</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalProjects}</p>
              <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                <span>{stats.ongoingProjects} ongoing</span>
              </p>
            </div>
            <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-[#3d82c4]" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Budget</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">₹{(stats.totalBudget / 10000000).toFixed(1)}Cr</p>
              <p className="text-xs text-gray-500 mt-2">Allocated funds</p>
            </div>
            <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center">
              <DollarSign className="w-7 h-7 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Beneficiaries</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalBeneficiaries}</p>
              <p className="text-xs text-gray-500 mt-2">{stats.disbursed} disbursed</p>
            </div>
            <div className="w-14 h-14 bg-purple-50 rounded-xl flex items-center justify-center">
              <Users className="w-7 h-7 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Pending</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pendingApprovals}</p>
              <p className="text-xs text-gray-500 mt-2">Awaiting approval</p>
            </div>
            <div className="w-14 h-14 bg-yellow-50 rounded-xl flex items-center justify-center">
              <Clock className="w-7 h-7 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Tabs */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex gap-6">
            {['projects', 'beneficiaries', 'schemes'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  pb-3 px-1 font-medium text-sm border-b-2 transition-colors capitalize
                  ${activeTab === tab
                    ? 'border-[#3d82c4] text-[#3d82c4]'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="disbursed">Disbursed</option>
              </select>
              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                <Download className="w-5 h-5" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'projects' && (
            <div className="space-y-4">
              {filteredProjects.map(project => (
                <div key={project.id} className="border border-gray-200 rounded-lg p-5 hover:border-[#3d82c4] transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-4 flex-1">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#3d82c4] to-[#2d6fad] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-800">{project.projectName}</h3>
                          <span className={`
                            px-2.5 py-1 rounded-full text-xs font-medium capitalize inline-flex items-center gap-1
                            ${project.status === 'completed' ? 'bg-green-100 text-green-700' : 
                              project.status === 'ongoing' ? 'bg-blue-100 text-blue-700' : 
                              'bg-yellow-100 text-yellow-700'}
                          `}>
                            {getStatusIcon(project.status)}
                            {project.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                          <div>
                            <p className="text-xs text-gray-500">Location</p>
                            <p className="font-medium text-gray-800">{project.location}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Budget</p>
                            <p className="font-medium text-gray-800">₹{project.budget}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Spent</p>
                            <p className="font-medium text-gray-800">₹{project.spent}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Beneficiaries</p>
                            <p className="font-medium text-gray-800">{project.beneficiaries}</p>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-600 font-medium">Progress</span>
                            <span className="font-semibold text-gray-800">{project.completion}%</span>
                          </div>
                          <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#3d82c4] to-[#2d6fad] rounded-full transition-all"
                              style={{ width: `${project.completion}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'beneficiaries' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Village</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Scheme</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Applied Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBeneficiaries.map(beneficiary => (
                    <tr key={beneficiary.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-800">{beneficiary.name}</p>
                          <p className="text-xs text-gray-500">{beneficiary.contact}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">{beneficiary.village}</td>
                      <td className="py-4 px-4">
                        <span className="inline-flex px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                          {beneficiary.scheme}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-semibold text-gray-800">₹{beneficiary.amount}</span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">{beneficiary.appliedDate}</td>
                      <td className="py-4 px-4">
                        <span className={`
                          inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize
                          ${beneficiary.status === 'disbursed' ? 'bg-green-100 text-green-700' :
                            beneficiary.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                            beneficiary.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }
                        `}>
                          {getStatusIcon(beneficiary.status)}
                          {beneficiary.status}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'schemes' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {schemes.map(scheme => (
                <div key={scheme.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">{scheme.name}</h3>
                        <p className="text-sm text-gray-600">{scheme.description}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Budget</p>
                      <p className="text-lg font-bold text-[#3d82c4]">₹{scheme.budget}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Beneficiaries</p>
                      <p className="text-lg font-bold text-green-600">{scheme.beneficiaries.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium">
                      <FileText className="w-4 h-4" />
                      View Details
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium">
                      <BarChart3 className="w-4 h-4" />
                      Analytics
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TNSSP;